import { AppBreakpoint, IAppNavBarSettings } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { CornerRadius, isColorVariant } from '@makyoapp/makyo-tw-ui'

export type NavPosition = 'none' | 'top' | 'bottom' | 'leading'

export type NavSpacing = 'spread' | 'close'

export type IconDirection = 'horizontal' | 'vertical'

export type MenuAlignment = 'leading' | 'center' | 'trailing'

export type LogoAlignment = 'leading' | 'center' | 'trailing'

export type NavLogo = {
  value: string
  label: string
}

export type DisplayOption = {
  label: string
  name: string
  checked: boolean
}

export interface NavbarSettings {
  title: string
  viewport: AppBreakpoint
  position: NavPosition
  spacing: NavSpacing
  iconDirection: IconDirection
  menuAlignment: MenuAlignment
  menuHeight: string
  menuWidth: string
  menuBgColor: string
  menuBgColorDark: string
  menuTextColor: string
  cornerRadius?: CornerRadius
  logoAlignment: LogoAlignment
  logoWidth: string
  maxItems: number
  logo: {
    value: string
    label: string
  }
  logoDark: {
    value: string
    label: string
  }
  displayOptions: DisplayOption[]
  show: boolean
}

export type BuilderNavbarSettings = Record<AppBreakpoint, NavbarSettings>

const viewportLabelMap: { [key: string]: string } = {
  desktop: 'Desktop/Laptop',
  phone: 'Phone (Portrait)',
  'phone-landscape': 'Phone (Landscape)',
  tablet: 'Tablet (Portrait)',
  'tablet-landscape': 'Tablet (Landscape)',
  tv: 'TV',
}

export const displayOptionsMap = [
  { label: 'Sticky', name: 'sticky' },
  { label: 'Logo', name: 'show_logo' },
  { label: 'Icon', name: 'show_icon' },
  { label: 'Label Text', name: 'show_label' },
  { label: 'Hover', name: 'show_hover' },
  { label: 'Shadow', name: 'show_shadow' },
  { label: 'Badge', name: 'show_badge' },
  { label: 'Active Indicator', name: 'show_active_indicator' },
]

export const fromSdkNavbarSettings = (settings: Record<AppBreakpoint, IAppNavBarSettings>): BuilderNavbarSettings => {
  return Object.entries(settings).reduce((acc, [viewport, config]) => {
    return {
      ...acc,
      [viewport]: {
        title: viewportLabelMap[viewport],
        viewport,
        position: config._Position as NavPosition,
        spacing: config._Spacing as NavSpacing,
        iconDirection: config._IconDirection as IconDirection,
        menuAlignment: config._MenuAlignment as MenuAlignment,
        logoAlignment: (config._LogoAlignment as LogoAlignment) || ('trailing' as LogoAlignment),
        menuHeight: config._MenuHeight || '70px',
        menuWidth: config._MenuWidth || '100%',
        logoWidth: config._LogoWidth || '50px',
        menuBgColor: isColorVariant(config._MenuBackgroundColor) ? config._MenuBackgroundColor : 'primary-20',
        menuBgColorDark: isColorVariant(config._MenuBackgroundDarkColor)
          ? config._MenuBackgroundDarkColor
          : 'primary-90',
        menuTextColor: config._MenuTextColor || 'on-primary-20',
        maxItems: config._MaxItems || 10,
        show: config._Show || false,
        //! ERROR: TextColor doesn't exist here, it's under Project.Appearance. Please fix it
        //! ERROR  menuTextColor: config._TextColor,
        cornerRadius: config._MenuShape as CornerRadius,
        logo: {
          value: config._Logo,
          label: config._LogoLabel,
        } as NavLogo,
        logoDark: {
          value: config._LogoDark,
          label: config._LogoLabel,
        } as NavLogo,
        displayOptions: displayOptionsMap.map((option) => {
          const specialKeys: { [key: string]: string } = {
            sticky: '_Sticky',
            show_label: '_ShowLabel',
          }
          const propertyKey =
            specialKeys[option.name] ||
            `_Show${option.label.replace('Show ', '').replace(/ (\w)/g, (_, c) => c.toUpperCase())}`
          return {
            ...option,
            checked: config[propertyKey as keyof IAppNavBarSettings] as boolean,
          }
        }),
      } as NavbarSettings,
    }
  }, {} as BuilderNavbarSettings)
}

export const toSdkNavbarSettings = (
  builderNavbar: BuilderNavbarSettings
): Record<AppBreakpoint, IAppNavBarSettings> => {
  const displayOptionsMap = {
    sticky: '_Sticky',
    show_logo: '_ShowLogo',
    show_icon: '_ShowIcon',
    show_label: '_ShowLabel',
    show_hover: '_ShowHover',
    show_shadow: '_ShowShadow',
    show_badge: '_ShowBadge',
    show_active_indicator: '_ShowActiveIndicator',
  }

  return Object.entries(builderNavbar).reduce(
    (acc, [viewport, settings]) => {
      return {
        ...acc,
        [viewport]: {
          _Position: settings.position,
          _Spacing: settings.spacing,
          _IconDirection: settings.iconDirection,
          _MenuAlignment: settings.menuAlignment,
          _LogoAlignment: settings.logoAlignment,
          _MenuHeight: settings.menuHeight,
          _MenuWidth: settings.menuWidth,
          _LogoWidth: settings.logoWidth,
          _Logo: settings.logo.value,
          _LogoDark: settings.logoDark.value,
          _LogoLabel: settings.logo.label,
          _MenuBackgroundColor: settings.menuBgColor,
          _MenuBackgroundDarkColor: settings.menuBgColorDark,
          _MenuTextColor: settings.menuTextColor,
          _MenuShape: settings.cornerRadius,
          _MaxItems: settings.maxItems,
          _Show: settings.show,
          ...settings.displayOptions.reduce((acc: any, option) => {
            const optionKey = displayOptionsMap[option.name as keyof typeof displayOptionsMap]
            acc[optionKey as keyof IAppNavBarSettings] = option.checked === true
            return acc
          }, {}),
        } as IAppNavBarSettings,
      }
    },
    {} as Record<AppBreakpoint, IAppNavBarSettings>
  )
}
